/**
 * CommentaryTV (BL-61) — the live-broadcast render surface for the operator-comms
 * arbiter. The bottom-left HUD is re-cut from a framed lit-CRT portrait into a
 * CRT *television* whose content is the running commentary: VERA (the ops ally)
 * and FENRIR (the wolf, enemy oracle) call the game live. ONE caller is on-air
 * full-frame at a time; the feed channel-switches VERA⇄FENRIR on a CRT cut as the
 * call passes, the whole set re-tinting to the live caller, and a lower-third
 * chyron types their line.
 *
 * This is a SURFACE, not new logic: it implements `MascotSurface`, so the SAME
 * `BeatScheduler`/`CommsDirector`/`commsContent` arbiter drives it — the lines,
 * the speaker, the priority/cooldowns are unchanged and un-duplicated. It renders
 * the real owner-approved VERA/FENRIR figures (the same inline `?raw` SVGs the
 * portrait loads, via `getMascotMarkup`) and reads the pose/expr the arbiter sets.
 *
 * Pure DOM + CSS (no canvas, no GL context, no arena coupling) → determinism-
 * neutral (render-only) and reduced-motion-safe (the comment still shows, instant,
 * with the correct caller; no roll/flicker/static-cut/type-on). Colour is central
 * (`AVATAR_PALETTE`/`POSE_COLOR`/`COLORS`) — no new gameplay hex.
 */
import { COLORS } from "../core/constants.ts";
import { t } from "../utils/i18n.ts";
import { AVATAR_PALETTE } from "../core/palette.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { UI_FONT_STACK, roleFontSize, withUiScale } from "./uiTokens.ts";
import {
  getMascotMarkup,
  preloadMascotArt,
  POSE_COLOR,
  type FenrirExpr,
  type MascotCharacter,
  type MascotSurface,
  type TvPopOutBeat,
  type VeraPose,
} from "./OperatorMascot.ts";

/** Per-caller broadcast identity — the channel banner + the NOW-LIVE role
 *  small. Kept here (not the arbiter) because it is pure surface chrome.
 *  S193 (BL-273): the never-rendered `desk` field is gone; the channel/role
 *  DESCRIPTORS are translatable copy (`comms.chrome.*`, the BL-257
 *  signalOverride precedent) while `LIVE`, `REC`, `CH-01/66`, `OP·XXX` and the
 *  caller NAMES stay broadcast chrome-by-design in every locale.
 *  D-334 (owner decision) — the chyron's own `VERA // ` / `FENRIR // ` name
 *  chip (the former `who` field + `.ctv-who`) is RETIRED: the NOW-LIVE tag
 *  (`.ctv-live`, below the bar) already names the on-air caller boldly, and
 *  the figure beside the caption already shows who is speaking — the chip
 *  was a redundant third cue that ate chyron width the caption needed (the
 *  reported "VERA // RUHIGE SCHICHT. NEIN,_" clipping read as one flowing
 *  line even though the chip and the caption were always separate DOM). */
interface CallerMeta {
  tag: string;
  channelKey: string;
  roleKey: string;
}

const CALLER_META: Record<MascotCharacter, CallerMeta> = {
  vera: {
    tag: "VERA",
    channelKey: "comms.chrome.channelVera",
    roleKey: "comms.chrome.roleOperator",
  },
  fenrir: {
    tag: "FENRIR",
    channelKey: "comms.chrome.channelFenrir",
    roleKey: "comms.chrome.roleOvermind",
  },
};

/** Canonical EN chrome — the fallback so a raw `comms.chrome.*` key can never
 *  render (the commsLine law, BL-89/BL-113). `translations.ts` carries DE. */
const CHROME_EN: Record<string, string> = {
  "comms.chrome.channelVera": "CH-01 · MAINFRAME FEED",
  "comms.chrome.channelFenrir": "CH-66 · HOSTILE INTERCEPT",
  "comms.chrome.roleOperator": "OPERATOR",
  "comms.chrome.roleOvermind": "OVERMIND",
  "comms.chrome.roleSignal": "SIGNAL",
};

function chrome(key: string): string {
  return t(key, undefined, CHROME_EN[key] ?? key);
}

/** S130 — ECHELON's sigil for the SIGNAL OVERRIDE stage: the S126 concentric
 *  iris, quoted small. Stroke-only, colored by the live accent var; the slow
 *  ≥1.2 s alpha forming (no pops, no strobe) follows the corruption bible §8. */
const ECHELON_SIGIL_MARKUP = `
  <svg class="ctv-sigil" viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="1.4" opacity="0.45"/>
    <circle cx="50" cy="50" r="29" fill="none" stroke="currentColor" stroke-width="1.8" opacity="0.65" stroke-dasharray="9 5"/>
    <circle cx="50" cy="50" r="18" fill="none" stroke="currentColor" stroke-width="2.2" opacity="0.85"/>
    <circle cx="50" cy="50" r="6" fill="currentColor" opacity="0.9"/>
  </svg>`;

/** Per-character type-on cadence (ms/char) — matches the signed concept. */
const TYPE_MS_PER_CHAR = 34;
/** How long the CRT channel-switch cut runs before the static clears. */
const SWITCH_MS = 320;
/** Gold sparkle count for the kernel_panic glitter burst (bounded → no DOM run). */
const GLITTER_COUNT = 16;
/** Task-013 (AC-17) — owner-feedback rework (D-327 amendment, 2026-07-31): a
 *  priority beat is now an IN-PLACE shake (scale + rotation wiggle), not the
 *  original large pop-out over the board. Short, matched to the
 *  `ctv-shake-run` keyframe. Next on-device tuning knob. */
const SHAKE_MS = 900;
/** Peak scale during the shake (1.05–1.10 per owner sketch). Tuning knob. */
const SHAKE_SCALE = 1.08;
/** Peak rotation (degrees) during the shake wiggle (±2–3° per owner sketch).
 *  Tuning knob. */
const SHAKE_ROTATE_DEG = 2.5;
/** Reduced-motion beat: a static glow pulse only, no scale/rotation. */
const SHAKE_STATIC_MS = 900;

export interface CommentaryTVOptions {
  /** Player initials shown on the broadcast as the operator-of-record tag. */
  operatorInitials?: string;
  /** Extra class for the root (e.g. the HUD dock class). */
  className?: string;
}

export class CommentaryTV implements MascotSurface {
  /** The mountable root (the TV bezel). Append this where the portrait was. */
  readonly el: HTMLElement;

  private readonly screen: HTMLElement;
  private readonly figure: HTMLElement;
  private readonly liveTag: HTMLElement;
  private readonly chyron: HTMLElement;
  private readonly line: HTMLElement;
  private readonly channelEl: HTMLElement;
  private readonly staticEl: HTMLElement;
  private readonly opEl: HTMLElement;
  private readonly tcEl: HTMLElement;

  private character: MascotCharacter = "vera";
  private pose: VeraPose = "idle";
  private fenrirExpr: FenrirExpr = "idle";
  private operatorInitials: string;

  private switchTimer: ReturnType<typeof setTimeout> | null = null;
  private tcTimer: ReturnType<typeof setInterval> | null = null;
  private popOutTimer: ReturnType<typeof setTimeout> | null = null;
  private tcSeconds = 2643; // a plausible on-air timecode seed (matches concept)

  constructor(opts: CommentaryTVOptions = {}) {
    injectStyles();
    this.operatorInitials = opts.operatorInitials ?? "AAA";

    this.el = document.createElement("div");
    this.el.className = `commentary-tv${opts.className ? ` ${opts.className}` : ""}`;
    this.el.setAttribute("aria-hidden", "true");
    // The feed is a passive monitor — never intercept a HUD click.
    this.el.style.pointerEvents = "none";
    if (isReducedMotion()) this.el.classList.add("ctv-reduced");

    // Power LED on the bezel.
    const pwr = document.createElement("span");
    pwr.className = "ctv-pwr";

    this.screen = document.createElement("div");
    this.screen.className = "ctv-screen";

    // ── broadcast top bar: REC · channel · operator-of-record · EQ · timecode ──
    const bar = document.createElement("div");
    bar.className = "ctv-bar";
    const rec = document.createElement("span");
    rec.className = "ctv-rec";
    rec.innerHTML = `<i></i>LIVE`;
    this.channelEl = document.createElement("span");
    this.channelEl.className = "ctv-ch";
    this.opEl = document.createElement("span");
    this.opEl.className = "ctv-op";
    const eq = document.createElement("span");
    eq.className = "ctv-eq";
    for (let i = 0; i < 7; i++) {
      const b = document.createElement("i");
      // Render-only Math.random (no sim/RNG coupling) → non-metronomic EQ.
      b.style.animationDelay = `${(-Math.random() * 0.9).toFixed(2)}s`;
      b.style.animationDuration = `${(0.6 + Math.random() * 0.6).toFixed(2)}s`;
      eq.appendChild(b);
    }
    this.tcEl = document.createElement("span");
    this.tcEl.className = "ctv-tc";
    bar.append(rec, this.channelEl, this.opEl, eq, this.tcEl);

    // ── NOW-LIVE caller tag (under the bar) ──
    this.liveTag = document.createElement("div");
    this.liveTag.className = "ctv-live";

    // ── the single STAGE (one caller, full-frame) ──
    const stage = document.createElement("div");
    stage.className = "ctv-stage";
    this.figure = document.createElement("div");
    this.figure.className = "ctv-fig";
    stage.appendChild(this.figure);

    // ── lower-third CHYRON (the typed line — D-334: no name chip; the
    //    NOW-LIVE tag + the figure already name the on-air caller) ──
    this.chyron = document.createElement("div");
    this.chyron.className = "ctv-chyron";
    const say = document.createElement("span");
    say.className = "ctv-say";
    this.line = document.createElement("span");
    this.line.className = "ctv-line";
    say.appendChild(this.line);
    this.chyron.append(say);

    // ── CRT fx overlays (channel-switch static, refresh-roll, scanlines, vignette) ──
    this.staticEl = document.createElement("div");
    this.staticEl.className = "ctv-static";
    const roll = document.createElement("div");
    roll.className = "ctv-roll";
    const scan = document.createElement("div");
    scan.className = "ctv-scan";
    const vignette = document.createElement("div");
    vignette.className = "ctv-vignette";

    this.screen.append(
      bar,
      this.liveTag,
      stage,
      this.chyron,
      this.staticEl,
      roll,
      scan,
      vignette,
    );
    this.el.append(pwr, this.screen);

    // Initial on-air state: VERA, idle, calm resting feed.
    this.applyCaller("vera", "idle");
    this.applyPoseTint("idle");
    this.renderFigure();
    this.startTimecode();
    // S193 (BL-275): warm cache + decoder for every on-air figure now, so a
    // mid-combat channel switch never fetches/decodes on the hot path.
    preloadMascotArt();
  }

  // ───────────────────────────── MascotSurface ────────────────────────────────

  setCharacter(character: MascotCharacter, fenrirExpr: FenrirExpr = "idle"): void {
    const changed = character !== this.character;
    this.fenrirExpr = fenrirExpr;
    // Mirror the portrait's setMascotCharacter: a swap resets the pose to idle;
    // a following setPose (VERA beats) overrides it.
    this.pose = "idle";
    this.applyCaller(character, fenrirExpr);
    this.renderFigure();
    // Re-tint the whole set to the live caller (FENRIR crimson / VERA pose hue).
    this.applyPoseTint("idle");
    // The signature: a CRT channel-cut whenever the call passes to a new caller.
    if (changed) this.playChannelSwitch();
  }

  setPose(pose: VeraPose): void {
    // No-op while FENRIR holds the surface (the boss-swap owns it) — matches the
    // portrait's setMascotPose guard exactly.
    if (this.character !== "vera") return;
    this.pose = pose;
    this.renderFigure();
    this.applyPoseTint(pose);
  }

  setQuip(line: string): void {
    this.typeLine(line);
  }

  clearQuip(): void {
    this.line.classList.remove("typing");
    this.line.textContent = "";
    this.el.removeAttribute("data-line");
  }

  pulseGlitch(): void {
    // FENRIR break-in interference. The cut is idempotent within its window, so
    // this folds cleanly into the channel-switch when the caller also flipped.
    this.playChannelSwitch();
  }

  clearGlitch(): void {
    // The CRT cut is self-clearing (timed); nothing to hold.
    if (this.switchTimer !== null) {
      clearTimeout(this.switchTimer);
      this.switchTimer = null;
    }
    this.screen.classList.remove("switching");
  }

  burstGlitter(): void {
    if (isReducedMotion()) return;
    for (let i = 0; i < GLITTER_COUNT; i++) {
      const s = document.createElement("span");
      s.className = "ctv-spark";
      const angle = Math.random() * Math.PI * 2;
      const dist = 24 + Math.random() * 40;
      s.style.setProperty("--gx", `${(Math.cos(angle) * dist).toFixed(1)}px`);
      s.style.setProperty("--gy", `${(Math.sin(angle) * dist - 14).toFixed(1)}px`);
      s.style.setProperty("--gd", `${(Math.random() * 0.26).toFixed(2)}s`);
      s.style.setProperty("--gsz", `${(5 + Math.random() * 6).toFixed(1)}px`);
      s.style.left = `${(40 + Math.random() * 20).toFixed(1)}%`;
      s.style.top = `${(34 + Math.random() * 22).toFixed(1)}%`;
      s.addEventListener("animationend", () => s.remove(), { once: true });
      this.figure.appendChild(s);
    }
  }

  /** S130 (Story Bible §6.3) — ECHELON's SIGNAL OVERRIDE: she takes the screen.
   *  The feed drops to her magenta, the channel reads as a foreign override,
   *  her sigil (the S126 face, quoted small) sits where a caller would stand,
   *  and the line types out. Pure chrome on the existing surface — the
   *  scheduler owns the hold and calls clearHijack when the feed snaps back. */
  playHijack(line: string): void {
    this.screen.classList.add("ctv-hijack");
    this.chyron.classList.add("ctv-hijack");
    this.el.style.setProperty("--ctv-ac", ECHELON.SIGNAL);
    // The channel banner + the role small are translatable copy
    // (BL-257/BL-273); "ECHELON" (the NOW-LIVE tag) is a proper name (kept
    // verbatim) — D-334: no separate chyron name chip any more, the NOW-LIVE
    // tag alone carries her identity during the override.
    this.channelEl.textContent = t("comms.signalOverride");
    this.liveTag.textContent = "ECHELON";
    const signalSmall = document.createElement("small");
    signalSmall.textContent = chrome("comms.chrome.roleSignal");
    this.liveTag.appendChild(signalSmall);
    this.figure.innerHTML = ECHELON_SIGIL_MARKUP;
    this.el.dataset.character = "echelon";
    this.playChannelSwitch();
    this.typeLine(line);
  }

  /** End the override — restore VERA's desk (the scheduler resets the caller
   *  state right after via setCharacter("vera"), so this is chrome-only). */
  clearHijack(): void {
    this.screen.classList.remove("ctv-hijack");
    this.chyron.classList.remove("ctv-hijack");
    this.playChannelSwitch();
  }

  /** Task-013 (AC-17/O-1+O-8) — a priority beat (boss spawn / new-signature
   *  enemy debut / breach) landed: the TV shakes IN PLACE (a brief scale-up +
   *  rotation wiggle), then settles back. Owner-feedback rework, 2026-07-31
   *  (D-327 amendment): the original build popped the TV out large over the
   *  board via `position: fixed`; the owner's sketch review judged that
   *  over-implemented and asked for the TV to stay docked in the rail and
   *  only slightly overhang the HUD boundary. Transform/opacity only
   *  (compositor-friendly, no per-frame JS). The overhang mechanism: the TV
   *  stays `position: relative` (its normal docked flow position) — no
   *  position change — but its own `#left-panel` ancestor clips via
   *  `overflow: hidden` (index.html), which would hide the few px of
   *  transform overhang the shake's scale/rotate produces at the rail edge.
   *  So for the shake's duration only, a class toggled on the nearest
   *  `#left-panel` ancestor (found via `closest`, a no-op outside that DOM —
   *  e.g. in unit tests) lifts that one clip; the TV's own `z-index` (raised
   *  only while shaking) keeps it painting above the board so the overhang
   *  actually reads instead of being covered by the canvas column. Reduced-
   *  motion: no scale/rotation change — a static glow pulse only (still
   *  pointer-events-transparent; never intercepts a HUD click).
   *  Re-triggerable: a second beat mid-shake restarts the animation cleanly. */
  popOutBeat(beat: TvPopOutBeat): void {
    this.el.dataset.popoutBeat = beat;
    if (this.popOutTimer !== null) {
      clearTimeout(this.popOutTimer);
      this.popOutTimer = null;
    }
    const rail = this.el.closest<HTMLElement>("#left-panel");
    if (isReducedMotion()) {
      this.el.classList.remove("ctv-shake");
      rail?.classList.remove("ctv-shake-overflow");
      this.el.classList.remove("ctv-shake-static");
      void this.el.offsetWidth; // reflow so the pulse re-triggers
      this.el.classList.add("ctv-shake-static");
      this.popOutTimer = setTimeout(() => {
        this.popOutTimer = null;
        this.el.classList.remove("ctv-shake-static");
      }, SHAKE_STATIC_MS);
      return;
    }
    this.el.classList.remove("ctv-shake-static");
    this.el.classList.remove("ctv-shake");
    void this.el.offsetWidth; // reflow so the keyframe re-triggers
    this.el.classList.add("ctv-shake");
    rail?.classList.add("ctv-shake-overflow");
    this.popOutTimer = setTimeout(() => {
      this.popOutTimer = null;
      this.el.classList.remove("ctv-shake");
      rail?.classList.remove("ctv-shake-overflow");
    }, SHAKE_MS);
  }

  // ───────────────────────────── extras + lifecycle ───────────────────────────

  /** Set the operator-of-record initials shown on the broadcast (player ID). */
  setOperatorInitials(initials: string): void {
    this.operatorInitials = initials || "AAA";
    this.opEl.textContent = `OP·${this.operatorInitials}`;
    // Only refresh the NOW-LIVE tag with the player's initials when VERA holds
    // the feed — never clobber FENRIR's on-air name.
    if (this.character !== "fenrir") {
      this.paintLiveTag(this.operatorInitials, chrome("comms.chrome.roleOperator"));
    }
  }

  /** Render the NOW-LIVE tag: the big name line + the small role descriptor. */
  private paintLiveTag(name: string, role: string): void {
    this.liveTag.textContent = name;
    const small = document.createElement("small");
    small.textContent = role;
    this.liveTag.appendChild(small);
  }

  /** Tear down timers + DOM (called from HUD.destroy). */
  destroy(): void {
    if (this.switchTimer !== null) clearTimeout(this.switchTimer);
    if (this.tcTimer !== null) clearInterval(this.tcTimer);
    if (this.popOutTimer !== null) clearTimeout(this.popOutTimer);
    this.switchTimer = null;
    this.tcTimer = null;
    this.popOutTimer = null;
    this.el.closest<HTMLElement>("#left-panel")?.classList.remove("ctv-shake-overflow");
    this.el.remove();
  }

  // ───────────────────────────── internals ────────────────────────────────────

  /** Swap the broadcast identity (channel, live tag, badge vars, introspection
   *  dataset) to a caller. Pure chrome — no figure render here. D-334: no
   *  chyron name chip any more — the NOW-LIVE tag (below) is the identity
   *  cue. */
  private applyCaller(character: MascotCharacter, fenrirExpr: FenrirExpr): void {
    this.character = character;
    const meta = CALLER_META[character];
    // The NOW-LIVE tag shows the on-air caller's identity: the player's OPERATOR
    // initials while VERA holds the feed (the player runs the defense), but
    // FENRIR's OWN name while the wolf is on-air — the enemy is not the operator.
    this.paintLiveTag(
      character === "fenrir" ? meta.tag : this.operatorInitials,
      chrome(meta.roleKey),
    );
    this.channelEl.textContent = chrome(meta.channelKey);
    this.opEl.textContent = `OP·${this.operatorInitials}`;
    this.screen.classList.toggle("ctv-fenrir", character === "fenrir");
    this.chyron.classList.toggle("ctv-fenrir", character === "fenrir");
    // Backing-emblem fills the SVGs read via CSS vars (same as the portrait).
    if (character === "fenrir") {
      this.figure.style.setProperty("--avatar-badge", AVATAR_PALETTE.fenrir.badge);
      this.figure.style.removeProperty("--avatar-badge-back");
    } else {
      this.figure.style.setProperty("--avatar-badge", AVATAR_PALETTE.vera.badge);
      this.figure.style.setProperty(
        "--avatar-badge-back",
        AVATAR_PALETTE.vera.badgeBack,
      );
    }
    // Introspection (and the test contract): mirror the portrait's dataset keys.
    this.el.dataset.character = character;
    this.el.dataset.pose = "idle";
    if (character === "fenrir") this.el.dataset.fenrirExpr = fenrirExpr;
    else delete this.el.dataset.fenrirExpr;
  }

  /** Re-tint the whole set to the moment: FENRIR crimson, VERA per-pose status
   *  (idle/cheer green, alert amber, alarm/down red, wow gold). One central
   *  source (`POSE_COLOR`/`AVATAR_PALETTE`) — no second pose→colour table. */
  private applyPoseTint(pose: VeraPose): void {
    const accent =
      this.character === "fenrir" ? AVATAR_PALETTE.fenrir.frame : POSE_COLOR[pose];
    this.el.style.setProperty("--ctv-ac", accent);
    this.el.dataset.pose = this.character === "fenrir" ? "idle" : pose;
    this.screen.classList.toggle("ctv-wow", pose === "wow" && this.character === "vera");
  }

  private renderFigure(): void {
    this.figure.innerHTML = getMascotMarkup(
      this.character,
      this.pose,
      this.fenrirExpr,
    );
  }

  /** Type the line into the chyron. The caption can wrap to 2 lines (Bug 6), so
   *  the type-on is a per-character reveal: each glyph is its own inline span that
   *  fades in on a stagger. `this.line.textContent` still concatenates to the full
   *  caption (a11y + tests), and the inline spans wrap naturally across both rows.
   *  Reduced-motion = instant, full, no spans/animation. */
  private typeLine(line: string): void {
    this.el.dataset.line = line;
    this.line.classList.remove("typing");
    if (isReducedMotion()) {
      this.line.textContent = line; // full caption, instant, no type-on
      return;
    }
    this.line.textContent = "";
    const frag = document.createDocumentFragment();
    for (let i = 0; i < line.length; i++) {
      const ch = document.createElement("span");
      // Bug fix (2026-07-31, D-329): this MUST be a distinct class from the
      // channel banner's `.ctv-ch` (`this.channelEl.className = "ctv-ch"`
      // above) — they used to collide, and the banner's single-line
      // `white-space: nowrap` rule cascaded onto every per-glyph span,
      // turning every SPACE character in the caption into a non-breaking
      // space. That silently defeated the 2-line wrap on `.ctv-line` (the
      // caption rendered as one unbreakable run and got clipped by the
      // line-clamp/overflow at the TV's right edge — the reported
      // "VERA // RUHIGE SCHICHT. NEI" truncation). See `.ctv-glyph` below.
      ch.className = "ctv-glyph";
      ch.textContent = line[i]!;
      ch.style.animationDelay = `${i * TYPE_MS_PER_CHAR}ms`;
      frag.appendChild(ch);
    }
    this.line.appendChild(frag);
    void this.line.offsetWidth; // reflow so the reveal keyframe re-triggers
    this.line.classList.add("typing");
  }

  /** The CRT channel-cut: a brief static burst + a horizontal sync-tear. */
  private playChannelSwitch(): void {
    if (isReducedMotion()) return; // instant swap; figure/tint already applied
    this.screen.classList.remove("switching");
    void this.screen.offsetWidth; // reflow so the cut keyframes re-trigger
    this.screen.classList.add("switching");
    if (this.switchTimer !== null) clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => {
      this.switchTimer = null;
      this.screen.classList.remove("switching");
    }, SWITCH_MS);
  }

  private startTimecode(): void {
    this.paintTimecode();
    if (typeof setInterval === "undefined") return;
    this.tcTimer = setInterval(() => {
      this.tcSeconds++;
      this.paintTimecode();
    }, 1000);
  }

  private paintTimecode(): void {
    const h = String(((this.tcSeconds / 3600) | 0) % 24).padStart(2, "0");
    const m = String(((this.tcSeconds / 60) | 0) % 60).padStart(2, "0");
    const s = String(this.tcSeconds % 60).padStart(2, "0");
    this.tcEl.textContent = `${h}:${m}:${s}`;
  }
}

// ─────────────────────────────── styles ───────────────────────────────────────
//
// Self-contained: the TV defines its own keyframes (it must render even if no
// framed mascot was ever created, so it cannot rely on OperatorMascot's injected
// styles). Colour comes only from the central palette via the `--ctv-ac` accent
// var + `color-mix` glows — no new gameplay hex.

const GREEN = COLORS.SENTINEL_GREEN;

function injectStyles(): void {
  if (
    typeof document === "undefined" ||
    document.getElementById("commentary-tv-styles")
  ) {
    return;
  }
  const style = document.createElement("style");
  style.id = "commentary-tv-styles";
  style.textContent = `
    .commentary-tv {
      --ctv-ac: ${GREEN};
      /* D-330-follow-up (verifier finding on D-329): the chyron's caption
         font-size is --ui-scale/--chrome-scale-aware (roleFontSize/
         withUiScale), but D-329 reserved the chyron's height as a FIXED px
         constant per tier — sized against --ui-scale: 1, which the app
         never actually ships at (L_UI_SCALE = 1.25, seeded at boot). Fixed
         px drifts every time a scale changes; a shared token that both the
         caption's font-size AND the chyron's height derive from cannot drift.
         --ctv-cap-size mirrors the SAME expression .ctv-line uses for its
         font-size (base tier: the 'body' role, 16px; the phone tier
         overrides it below to the 18px it already used) — see uiTokens.ts
         withUiScale. --ctv-chyron-h is .ctv-line's own N-line box (its
         line-height: 1.3) plus the chyron's own padding-bottom (5px) and
         border-top (1px) — i.e. the exact content-box arithmetic the chyron
         needs, expressed once and read by both .ctv-chyron's height and
         .ctv-stage's bottom (so the two can never fall out of lockstep —
         the D-327 figure/caption non-overlap invariant).
         D-334 (owner decision, "let the commentary TV grow") — the line
         count is now 3 (was 2, D-329): with the chyron's own name chip gone
         (see CALLER_META above) the freed dock height goes to the caption
         instead of clipping it. STILL derived, never a fixed px — see
         the D-329 defect this guards against. Keep .ctv-line's own
         -webkit-line-clamp (below) equal to this multiplier. */
      --ctv-cap-size: ${roleFontSize("body")};
      --ctv-chyron-lines: 3;
      --ctv-chyron-h: calc(var(--ctv-cap-size) * 1.3 * var(--ctv-chyron-lines) + 6px);
      position: relative;
      /* Bug 1/Bug 15: own stacking context so the TV's internal z-indices (5–9)
         are scoped and can never punch through #ui-layer onto the pre-game or
         game-over screens. Harmless in-game (only internal z is used). */
      isolation: isolate;
      display: flex;
      box-sizing: border-box;
      padding: 7px 7px 9px;
      border-radius: 14px / 12px;
      background: linear-gradient(180deg, #15191a, #0a0c0d 60%, #070808);
      /* BL-214 (S189) — a stronger accent halo so the live-broadcast TV reads
         as a present, premium element instead of getting lost in the rail. */
      box-shadow:
        0 0 0 1px #2a3133, 0 0 0 2px #000,
        0 0 24px color-mix(in srgb, var(--ctv-ac) 28%, transparent),
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        0 20px 44px -20px #000;
      transition: box-shadow 0.4s ease;
      line-height: 1.4;
      font-family: ${UI_FONT_STACK};
    }
    /* tiny bezel speaker-grille + power LED */
    .commentary-tv::before {
      content: ""; position: absolute; right: 14px; bottom: 3px;
      width: 38px; height: 3px; border-radius: 2px; opacity: 0.8;
      background: repeating-linear-gradient(90deg, #000 0 2px, #1d2426 2px 4px);
    }
    .commentary-tv .ctv-pwr {
      position: absolute; left: 13px; bottom: 2.5px; width: 6px; height: 6px;
      border-radius: 50%; background: var(--ctv-ac);
      box-shadow: 0 0 7px var(--ctv-ac); animation: ctv-pwr 3s ease-in-out infinite;
    }
    @keyframes ctv-pwr { 0%,100%{opacity:0.85} 50%{opacity:0.4} }

    .commentary-tv .ctv-screen {
      position: relative; flex: 1 1 auto; min-width: 0; overflow: hidden;
      border-radius: 9px / 13px;
      background: radial-gradient(120% 130% at 50% 42%, #0b1410 0%, #060a07 70%, #020403 100%);
      box-shadow: inset 0 0 18px rgba(0,0,0,0.9), inset 0 0 48px rgba(0,0,0,0.6);
      outline: 1px solid color-mix(in srgb, var(--ctv-ac) 12%, transparent);
    }

    /* ── broadcast top bar ── */
    .commentary-tv .ctv-bar {
      position: absolute; left: 0; right: 0; top: 0; z-index: 6;
      display: flex; align-items: center; gap: 6px; padding: 3px 7px;
      font-size: ${roleFontSize("micro")}; letter-spacing: 0.1em; color: #9fb0a4;
      background: linear-gradient(180deg, rgba(0,0,0,0.6), transparent);
    }
    .commentary-tv .ctv-rec {
      display: flex; align-items: center; gap: 4px; color: ${COLORS.ORACLE_RED}; flex: none;
    }
    .commentary-tv .ctv-rec i {
      width: 6px; height: 6px; border-radius: 50%; background: ${COLORS.ORACLE_RED};
      box-shadow: 0 0 6px ${COLORS.ORACLE_RED}; animation: ctv-rec 1.1s steps(2, end) infinite;
    }
    @keyframes ctv-rec { 0%,50%{opacity:1} 51%,100%{opacity:0.15} }
    .commentary-tv .ctv-ch {
      color: color-mix(in srgb, var(--ctv-ac) 80%, #ffffff);
      overflow: hidden; white-space: nowrap; text-overflow: ellipsis; min-width: 0;
    }
    .commentary-tv .ctv-op {
      flex: none; color: #6f8a72; font-size: ${roleFontSize("micro")}; letter-spacing: 0.08em;
    }
    .commentary-tv .ctv-eq {
      display: flex; align-items: flex-end; gap: 1.5px; height: 10px; margin-left: auto; flex: none;
    }
    .commentary-tv .ctv-eq i {
      width: 2px; background: var(--ctv-ac); border-radius: 1px;
      box-shadow: 0 0 4px var(--ctv-ac); animation: ctv-eq 0.9s ease-in-out infinite;
    }
    @keyframes ctv-eq { 0%,100%{height:2px;opacity:0.5} 50%{height:10px;opacity:1} }
    .commentary-tv .ctv-tc {
      flex: none; color: #6f8a72; font-size: ${roleFontSize("micro")}; letter-spacing: 0.04em;
      font-variant-numeric: tabular-nums;
    }

    /* ── NOW-LIVE caller tag ── */
    .commentary-tv .ctv-live {
      position: absolute; left: 7px; top: 18px; z-index: 5;
      /* BL-214 — the current-speaker line is the "who's talking" anchor: bolder
         + a brighter accent halo so VERA / FENRIR reads at a glance on iPhone. */
      font-size: ${roleFontSize("body")}; font-weight: 700;
      letter-spacing: 0.1em; line-height: 1;
      color: var(--ctv-ac); text-shadow: 0 0 12px color-mix(in srgb, var(--ctv-ac) 80%, transparent);
    }
    .commentary-tv .ctv-live small {
      display: block; font-size: ${roleFontSize("micro")}; letter-spacing: 0.16em; color: #7f9a86; margin-top: 2px;
    }

    /* ── the stage (one caller, full-frame) ── */
    .commentary-tv .ctv-stage {
      position: absolute; left: 0; right: 0; top: 16px; bottom: var(--ctv-chyron-h);
      z-index: 3;
      display: flex; align-items: flex-end; justify-content: center; overflow: hidden;
    }
    .commentary-tv .ctv-stage::before {
      content: ""; position: absolute; left: 50%; bottom: 2px; width: 74%; height: 70%;
      transform: translateX(-50%); z-index: 0;
      background: radial-gradient(60% 100% at 50% 100%,
        color-mix(in srgb, var(--ctv-ac) 18%, transparent), transparent 72%);
    }
    .commentary-tv .ctv-fig {
      position: relative; z-index: 1; height: 100%; aspect-ratio: 1 / 1;
      overflow: hidden; transform: translateY(6px); animation: ctv-breathe 4.4s ease-in-out infinite;
    }
    /* S144: the on-air figure is raster character art (<img>) — object-fit:cover
       biased to the top fills the square stage as head-and-shoulders. (The
       ECHELON SIGNAL-OVERRIDE sigil is a separate inline <svg>, sized by
       .ctv-sigil below.) */
    .commentary-tv .ctv-fig img {
      display: block; width: 100%; height: 100%;
      object-fit: cover; object-position: 50% 6%;
      -webkit-user-drag: none;
    }
    @keyframes ctv-breathe { 0%,100%{transform:translateY(6px)} 50%{transform:translateY(3px)} }

    /* ── lower-third chyron (name chip + typed line) ── */
    .commentary-tv .ctv-chyron {
      position: absolute; left: 0; right: 0; bottom: 0; z-index: 6;
      /* verifier finding on D-329: that fix's 50px/64px were sized against
         --ui-scale: 1, but the app ships L-only (--ui-scale: 1.25,
         src/utils/uiScale.ts), plus the phone tier's own --chrome-scale
         (up to 1.5, stageLayout.ts CHROME_SCALE_MAX) — at the SHIPPED
         scale, 50px/64px were 6-30px short of a real 2-line caption and the
         owner's 2nd line clipped. height: var(--ctv-chyron-h) (declared on
         .commentary-tv above) derives the reservation from .ctv-line's
         own type metrics instead, so it tracks EVERY scale automatically —
         no more per-tier magic px to redo each time a scale changes. */
      height: var(--ctv-chyron-h);
      /* CR-2: a touch more inner padding (was 0 7px, no bottom) so the 2nd caption
         row clears the CRT's own rounded bottom corners + the iPhone display
         corner under the dock. */
      display: flex; align-items: center; gap: 6px; padding: 0 10px 5px;
      background: linear-gradient(0deg, rgba(2,5,3,0.93), rgba(2,5,3,0.55) 70%, transparent);
      border-top: 1px solid color-mix(in srgb, var(--ctv-ac) 45%, transparent);
    }
    /* D-334 — no more chyron name chip (S193/BL-273's .ctv-who is retired):
       the NOW-LIVE tag above the stage + the figure beside the caption already
       name the on-air caller, so .ctv-say (the caption) now owns the full
       chyron width. */
    .commentary-tv .ctv-say {
      flex: 1 1 auto; min-width: 0; overflow: hidden;
    }
    /* Bug 6: the caption wraps up to 3 full-width lines (D-334; was 2, D-329 —
       see --ctv-chyron-lines above). The broadcast type-on is a per-character
       fade (each .ctv-glyph span reveals on a stagger), which wraps naturally
       — unlike the old single-line width reveal. */
    .commentary-tv .ctv-line {
      display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3;
      /* D-329: word-boundary wrap only (overflow-wrap: normal, no
         word-break/hyphenation) — a word never breaks mid-character. If a
         caption still overflows past 3 lines, the line-clamp's own
         text-overflow:ellipsis degrades it gracefully instead of a mid-word
         cut or a layout push. */
      max-width: 100%; overflow: hidden; text-overflow: ellipsis;
      overflow-wrap: normal; word-break: normal; white-space: normal;
      vertical-align: bottom;
      /* BL-214 — a touch bolder + a stronger dark backing so the comment stays
         crisp against the CRT glow on a small phone screen (no size change, so
         the 2-line clamp + figure are untouched — no clipping). */
      font-size: var(--ctv-cap-size); font-weight: 600;
      letter-spacing: 0.02em; line-height: 1.3;
      color: #eafff0; text-shadow: 0 1px 4px rgba(0,0,0,0.95);
    }
    .commentary-tv .ctv-line .ctv-glyph { opacity: 1; }
    .commentary-tv .ctv-line.typing .ctv-glyph { opacity: 0; animation: ctv-charin 0.16s ease-out forwards; }
    @keyframes ctv-charin { from { opacity: 0 } to { opacity: 1 } }

    /* CR-2 — iPhone: the taller dock (html.phone-tier --hud-comms-footprint-h)
       enlarges the on-air figure; give the broadcast TEXT its share too — a taller
       lower-third + bigger caption / now-live type so VERA & FENRIR read on the
       small screen. The stage bottom tracks the chyron height so they never
       overlap. iPad/desktop (no .phone-tier) are unchanged. */
    /* Task-013 (AC-17/O-1) — base-size correction: the taller
       html.phone-tier --hud-comms-footprint-h dock (index.html, bumped
       268→292px alongside this) grows the chyron further so a full 2-line
       caption at the phone type size never overlaps the figure/stage above
       it. Verifier finding on D-329: raising --ctv-cap-size here (18px
       base, same --ui-scale/--chrome-scale-aware expression .ctv-line
       already read on the phone tier) is enough — --ctv-chyron-h on
       .commentary-tv above re-resolves from the NEW --ctv-cap-size
       automatically (custom properties substitute at used-value time against
       the element's own cascaded value), so .ctv-chyron's height and
       .ctv-stage's bottom both grow with it — no separate height/bottom
       override needed here any more, and no risk of the two drifting apart. */
    html.phone-tier .commentary-tv { --ctv-cap-size: ${withUiScale(18)}; }
    html.phone-tier .commentary-tv .ctv-live { font-size: ${withUiScale(18)}; }

    /* ── CRT fx ── */
    .commentary-tv .ctv-scan {
      position: absolute; inset: 0; z-index: 7; pointer-events: none;
      background: repeating-linear-gradient(0deg, rgba(0,0,0,0.2) 0 1px, transparent 1px 3px);
    }
    .commentary-tv .ctv-vignette {
      position: absolute; inset: 0; z-index: 9; pointer-events: none; border-radius: 9px / 13px;
      background: radial-gradient(130% 120% at 50% 50%, transparent 56%, rgba(0,0,0,0.5) 100%);
    }
    .commentary-tv .ctv-roll {
      position: absolute; left: 0; right: 0; height: 46%; z-index: 7; pointer-events: none;
      mix-blend-mode: screen; opacity: 0.06;
      background: linear-gradient(180deg, transparent, rgba(255,255,255,0.8), transparent);
      animation: ctv-roll 6.5s linear infinite;
    }
    @keyframes ctv-roll { 0%{top:-50%} 100%{top:110%} }
    .commentary-tv .ctv-static {
      position: absolute; inset: 0; z-index: 8; pointer-events: none; opacity: 0; mix-blend-mode: screen;
      background:
        repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 2px),
        repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 1px, transparent 1px 3px);
      background-size: 100% 3px, 5px 100%;
    }
    .commentary-tv .ctv-screen.switching .ctv-static { animation: ctv-zap 0.26s steps(3, end); }
    .commentary-tv .ctv-screen.switching .ctv-stage { animation: ctv-tear 0.26s steps(4, end); }
    @keyframes ctv-zap {
      0%{opacity:0} 20%{opacity:0.9} 55%{opacity:0.5; background-position:0 7px,4px 0} 100%{opacity:0}
    }
    @keyframes ctv-tear {
      0%{transform:translateY(0)} 25%{transform:translate(6px,-6px) scaleY(1.05)}
      50%{transform:translate(-5px,3px)} 100%{transform:translateY(0)}
    }

    /* ── S130: the ECHELON SIGNAL OVERRIDE (Story Bible §6.3) ── */
    .commentary-tv .ctv-screen.ctv-hijack {
      background: radial-gradient(120% 130% at 50% 42%, #140912 0%, #0a050a 70%, #040204 100%);
      outline-color: color-mix(in srgb, var(--ctv-ac) 35%, transparent);
    }
    .commentary-tv .ctv-screen.ctv-hijack .ctv-rec,
    .commentary-tv .ctv-screen.ctv-hijack .ctv-rec i {
      color: var(--ctv-ac);
      background: var(--ctv-ac);
      box-shadow: 0 0 6px var(--ctv-ac);
    }
    .commentary-tv .ctv-sigil {
      width: 72%; height: 72%; margin: auto; display: block;
      color: var(--ctv-ac);
      filter: drop-shadow(0 0 8px color-mix(in srgb, var(--ctv-ac) 70%, transparent));
      opacity: 0;
      animation: ctv-sigil-form 1.6s ease-out forwards, ctv-sigil-breathe 7s ease-in-out 1.6s infinite;
    }
    /* §8-safe: a single ≥1.2 s form-in, then an ~7 s low-amplitude breathe. */
    @keyframes ctv-sigil-form { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
    @keyframes ctv-sigil-breathe { 0%,100% { opacity: 1; } 50% { opacity: 0.86; } }
    .commentary-tv.ctv-reduced .ctv-sigil { animation: none !important; opacity: 1; }
    @media (prefers-reduced-motion: reduce) {
      .commentary-tv .ctv-sigil { animation: none !important; opacity: 1; }
    }

    /* ── kernel_panic WOW: gold surge + glasses-fly + glitter ── */
    .commentary-tv .ctv-screen.ctv-wow {
      box-shadow:
        inset 0 0 18px rgba(0,0,0,0.9), inset 0 0 48px rgba(0,0,0,0.6),
        0 0 22px color-mix(in srgb, var(--ctv-ac) 60%, transparent);
      animation: ctv-gold-surge 0.6s ease-out;
    }
    @keyframes ctv-gold-surge {
      0%{ box-shadow: inset 0 0 18px rgba(0,0,0,0.9); }
      35%{ box-shadow: inset 0 0 18px rgba(0,0,0,0.9),
             0 0 34px color-mix(in srgb, var(--ctv-ac) 85%, #ffffff); }
      100%{ box-shadow: inset 0 0 18px rgba(0,0,0,0.9),
             0 0 22px color-mix(in srgb, var(--ctv-ac) 60%, transparent); }
    }
    /* S193 (BL-273): the SVG-era glasses-fly / lens-glint / wink-lid rules are
       retired — they targeted hooks inside the old inline SVG figures, which
       cannot exist inside the S144 raster <img>; the per-state ART carries the
       mood now (the WOW keeps its live gold surge + glitter). */
    .commentary-tv .ctv-spark {
      position: absolute; z-index: 4; pointer-events: none;
      width: var(--gsz, 7px); height: var(--gsz, 7px);
      background: radial-gradient(circle, #FFF7CC 0%, ${AVATAR_PALETTE.vera.wow} 50%, transparent 72%);
      clip-path: polygon(50% 0, 58% 42%, 100% 50%, 58% 58%, 50% 100%, 42% 58%, 0 50%, 42% 42%);
      filter: drop-shadow(0 0 4px ${AVATAR_PALETTE.vera.wow});
      opacity: 0; transform: translate(-50%, -50%) scale(0);
      animation: ctv-spark-pop 0.92s ease-out var(--gd, 0s) forwards;
    }
    @keyframes ctv-spark-pop {
      0%{opacity:0; transform:translate(-50%,-50%) scale(0.2) rotate(0deg)}
      28%{opacity:1; transform:translate(calc(-50% + var(--gx,0px)*0.45), calc(-50% + var(--gy,0px)*0.45)) scale(1) rotate(70deg)}
      100%{opacity:0; transform:translate(calc(-50% + var(--gx,0px)), calc(-50% + var(--gy,0px))) scale(0.15) rotate(180deg)}
    }

    /* Task-013 (AC-17): priority shake, in place (see popOutBeat doc). */
    .commentary-tv.ctv-shake {
      z-index: 40;
      animation: ctv-shake-run ${SHAKE_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
      transform-origin: 50% 50%;
    }
    @keyframes ctv-shake-run {
      0%   { transform: scale(1) rotate(0deg); }
      14%  { transform: scale(${SHAKE_SCALE}) rotate(-${SHAKE_ROTATE_DEG}deg); }
      30%  { transform: scale(${SHAKE_SCALE}) rotate(${SHAKE_ROTATE_DEG}deg); }
      46%  { transform: scale(1.05) rotate(-1.6deg); }
      62%  { transform: scale(1.03) rotate(1.2deg); }
      78%  { transform: scale(1.015) rotate(-0.6deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
    /* Reduced-motion: static glow pulse only, no scale/rotation. */
    .commentary-tv.ctv-shake-static::after {
      content: ""; position: absolute; inset: -6px; z-index: 10; pointer-events: none;
      border-radius: 14px / 12px;
      box-shadow: 0 0 46px 6px color-mix(in srgb, var(--ctv-ac) 85%, transparent);
      opacity: 0;
      animation: ctv-shake-static-pulse ${SHAKE_STATIC_MS}ms ease-out both;
    }
    @keyframes ctv-shake-static-pulse {
      0%   { opacity: 0; }
      35%  { opacity: 1; }
      100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .commentary-tv.ctv-shake { animation: none !important; z-index: auto; }
    }
    /* #left-panel clips (overflow: hidden, index.html); toggled on it for the
       shake's duration only (popOutBeat) so the overhang isn't clipped. */
    #left-panel.ctv-shake-overflow { overflow: visible; }

    /* ── reduced motion: kill roll/flicker/static/type-on/idle anims — the
       comment still shows (line + correct caller, instant), still a premium feed ── */
    .commentary-tv.ctv-reduced .ctv-roll,
    .commentary-tv.ctv-reduced .ctv-pwr,
    .commentary-tv.ctv-reduced .ctv-rec i,
    .commentary-tv.ctv-reduced .ctv-eq i,
    .commentary-tv.ctv-reduced .ctv-fig,
    .commentary-tv.ctv-reduced .ctv-line.typing,
    .commentary-tv.ctv-reduced .ctv-screen.switching .ctv-static,
    .commentary-tv.ctv-reduced .ctv-screen.switching .ctv-stage,
    .commentary-tv.ctv-reduced .ctv-screen.ctv-wow {
      animation: none !important;
    }
    .commentary-tv.ctv-reduced .ctv-line .ctv-glyph { opacity: 1; animation: none !important; }

    @media (prefers-reduced-motion: reduce) {
      .commentary-tv .ctv-roll,
      .commentary-tv .ctv-pwr,
      .commentary-tv .ctv-rec i,
      .commentary-tv .ctv-eq i,
      .commentary-tv .ctv-fig,
      .commentary-tv .ctv-line.typing,
      .commentary-tv .ctv-screen.switching .ctv-static,
      .commentary-tv .ctv-screen.switching .ctv-stage,
      .commentary-tv .ctv-screen.ctv-wow {
        animation: none !important;
      }
      .commentary-tv .ctv-line .ctv-glyph { opacity: 1; animation: none !important; }
    }
  `;
  document.head.appendChild(style);
}
